import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  GlobalOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons"
import type { TableColumnsType, TablePaginationConfig, TableProps } from "antd"
import { Button, Card, message, Modal, Select, Space, Table, Tabs, Tag, Tooltip } from "antd"
import Title from "antd/es/typography/Title"
import queryString from "query-string"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router"
import { Link } from "react-router-dom"
import Filters, { FilterComponent } from "~/components/Filters"
import { Paths } from "~/constants"
import { t } from "~/languages/i18n"
import { AttributesPage, PageData, PaginationMeta } from "~/types/types"
import { formatDatePost } from "~/utils/formatDate"
import { normalizeText } from "~/utils/helper"
import logoEn from "../../../../public/images/logo-en.png"
import logoVi from "../../../../public/images/logo-vi.png"
import { RootState } from "../../auth/services/redux/store"
import { deleteTemplate } from "../../block-content/service/blockContent"
import { useUiBuilder } from "../../ui-builder/external-components"
import pageService from "../services/page"

const List: React.FC = () => {
  const [dataItem, setDataItem] = useState<PageData[]>([])
  const filterRef = useRef<{ filterFunction: () => void } | null>(null)
  const [sortPaginationValues, setSortPaginationValues] = useState<{ [key: string]: string } | null>(null)

  const [needRefresh, setNeedRefresh] = useState<boolean>(false)
  const [filterValues, setFilterValues] = useState<{ [key: string]: string } | null>(null)
  const [defaultFilterValues, setDefaultFilterValues] = useState<{ [key: string]: any } | null>(null)

  const [pageNameOptions, setPageNameOptions] = useState<{ value: string | number; label: string }[]>([])
  const [filterTabPage, setFilterTabPage] = useState("all")
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])

  const [bulkAction, setBulkAction] = useState<string>("")
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)

  const [metaData, setMetaData] = useState<PaginationMeta>()
  const [totalDraftsItem, setTotalDraftsItem] = useState(0)
  const [totalPublishedItem, setTotalPublishedItem] = useState(0)

  const [totalItem, setTotalItem] = useState(0)
  const [pageItemData, setPageItemData] = useState<PageData>()
  const account = useSelector((state: RootState) => state.account)

  const pageServices = pageService()
  const { createNewBlockContent } = useUiBuilder()

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(selectedRowKeys)
    },
  }

  const fetchAllPages = async (searchTerms?: { [key: string]: string }) => {
    setLoading(true)
    setDataItem([])

    try {
      const defaultPagination = { page: "1", pageSize: "20" }
      const allResponse = await pageServices.fetchAllPages({ ...defaultPagination, ...searchTerms })
      const response = allResponse && allResponse.data
      const meta = allResponse && allResponse.meta
      setMetaData(meta)

      if (response && response.length > 0) {
        setDataItem(response)
        if (searchTerms === undefined) {
          setTotalItem(meta?.pagination?.total || 0)
        }
        setPageNameOptions(
          response.map((page: { id: number; attributes: AttributesPage }) => ({
            value: page.id,
            label: page.attributes.name,
          })),
        )

        setLoading(false)
      } else {
        setLoading(false)
      }

      return response
    } catch (error) {
      message.error(t("Failed to fetch page. Please try again"))
    }
  }

  const onSearch = useCallback(
    (filters: object) => {
      const values: { [key: string]: string } = {}

      Object.keys(filters).forEach(key => {
        const value = filters[key as keyof typeof filters]
        if (typeof value === "string") {
          values[key] = value
        }
      })

      if (!values) {
        message.error(t("Values ​​are not defined"))
        return
      }

      let newValue = { ...values }

      if (values.search) {
        newValue = { ...newValue, search: normalizeText(values.search) }
      }

      const searchParams = queryString.stringify(newValue, { encode: false })
      const newUrl = searchParams.length > 0 ? `${location.pathname}?${searchParams}` : location.pathname
      navigate(newUrl)
    },
    [navigate],
  )

  useEffect(() => {
    const searchParams = queryString.parse(location.search)
    const filterValues: { [key: string]: string } = {}
    const sortPaginationValues: { [key: string]: string } = {}

    for (const key of Object.keys(searchParams)) {
      const value = searchParams[key]
      if (value && typeof value === "string") {
        if (key === "page" || key === "limit" || value === "asc" || value === "desc") {
          sortPaginationValues[key] = value
        } else {
          filterValues[key] = value
        }
      }
    }

    setSortPaginationValues(sortPaginationValues)
    setFilterValues(filterValues)
    setDefaultFilterValues(filterValues)
  }, [location.search])

  useEffect(() => {
    fetchAllPages()
  }, [needRefresh])

  useEffect(() => {
    if (!filterValues && !sortPaginationValues) return

    if (filterValues || sortPaginationValues) {
      const params = { ...filterValues, ...sortPaginationValues, publicationState: filterTabPage }
      fetchAllPages(params)
    }
  }, [filterValues, sortPaginationValues])

  const handleEdit = (id: number) => {
    navigate(`${Paths.PAGES.EDIT.PATH}/${id}`)
  }

  const getPageInformaiton = async (id: number) => {
    try {
      const res = await pageServices.fetchPageById(id)
      setPageItemData(res)
    } catch (error) {
      message.error(t("Fetch page error!"))
    }
  }
  const isSlugUnique = async (slug: string): Promise<boolean> => {
    try {
      const response = await pageServices.checkSlug(slug)
      return response.data.length === 0
    } catch {
      return false
    }
  }

  const generateUniqueSlug = async (baseSlug: string, baseName: string): Promise<{ slug: string; name: string }> => {
    let newSlug = baseSlug
    let newName = baseName
    let count = 1

    const match = newSlug.match(/-copy-(\d+)$/)
    if (match) {
      count = parseInt(match[1], 10) + 1
      newSlug = `${baseSlug.replace(/-copy-(\d+)$/, "")}-copy-${count}`
      newName = `${baseName.replace(/-copy-(\d+)$/, "")}-copy-${count}`
    } else {
      newSlug = `${baseSlug}-copy-1`
      newName = `${baseName}-copy-1`
    }

    while (!(await isSlugUnique(newSlug))) {
      count++
      newSlug = `${baseSlug.replace(/-copy-\d*$/, "")}-copy-${count}`
      newName = `${baseName.replace(/-copy-\d*$/, "")}-copy-${count}`
    }

    return { slug: newSlug, name: newName }
  }

  useEffect(() => {
    if (pageItemData) {
      handleDuplicate()
    }
  }, [pageItemData])

  const handleDuplicate = async () => {
    if (pageItemData) {
      const blockContentData = pageItemData.attributes.blockContent.data.attributes
      const baseSlug = pageItemData.attributes.slug
      const baseName = pageItemData.attributes.name
      const { slug: uniqueSlug, name: uniqueName } = await generateUniqueSlug(baseSlug, baseName)
      try {
        const createdBlockContent = await createNewBlockContent(blockContentData)

        const dataPayload = {
          author: account.user,
          name: uniqueName,
          slug: uniqueSlug,
          content: pageItemData.attributes.content,
          publishedAt: pageItemData.attributes.publishedAt
            ? new Date(pageItemData.attributes.publishedAt).toISOString()
            : null,
          image: pageItemData.attributes.image.data || null,
          SEO: {
            id: 0,
            metaTitle: pageItemData.attributes.SEO?.metaTitle,
            metaDescription: pageItemData.attributes.SEO?.metaDescription,
          },
          isUseUIBuilder: pageItemData.attributes.isUseUIBuilder,
          blockContent: createdBlockContent.data,
        }

        await pageServices.addPage({ data: dataPayload })
        message.success({ content: t("Page duplicated successfully!"), duration: 2 })
        setPageItemData(undefined)
        fetchAllPages()
      } catch (error) {
        message.error(t("Failed to duplicate page. Please try again."))
      }
    }
  }

  const handleDelete = async (id: number) => {
    const res = await pageServices.fetchPageById(id)

    Modal.confirm({
      title: t("Confirm Delete"),
      content: t("Are you sure you want to delete this page?"),
      okText: t("Delete"),
      cancelText: t("Cancel"),
      onOk: async () => {
        try {
          setLoading(true)
          await pageServices.deletePage(id)
          await deleteTemplate(res.attributes.blockContent.data.id)
          fetchAllPages()
          message.success(t("The page has been successfully deleted!"))
        } catch {
          message.error(t("Handle Delete Error"))
        }
      },
    })
  }

  const handleBulkDelete = async () => {
    try {
      setLoading(true)
      const ids = selectedRowKeys.map(id => Number(id))
      await Promise.all(ids.map(id => pageServices.deletePage(id)))
      setSelectedRowKeys([])
      fetchAllPages()
      setLoading(false)
      message.success(t("Pages deleted successfully!"))
    } catch {
      message.error(t("Handle Delete Error"))
    }
  }

  const handleApplyBulkAction = () => {
    if (bulkAction === "delete" && selectedRowKeys.length > 0) {
      Modal.confirm({
        title: t("Confirm Delete"),
        content: t("Are you sure you want to delete the selected pages?"),
        okText: t("Delete"),
        cancelText: t("Cancel"),
        onOk: handleBulkDelete,
      })
    } else if (selectedRowKeys.length === 0) {
      message.warning(t("Please select at least one page to delete."))
    } else {
      message.warning(t("Please select a valid bulk action"))
    }
  }

  const handleChangeTab = (key: string) => {
    setFilterTabPage(key)

    if (filterRef.current) {
      filterRef.current.filterFunction()
    }
    setSortPaginationValues(null)
    fetchAllPages({
      ...filterValues,
      ...sortPaginationValues,
      publicationState: key,
    })
  }

  const onChange: TableProps<PageData>["onChange"] = (pagination: TablePaginationConfig) => {
    setSortPaginationValues({
      page: pagination.current ? pagination.current.toString() : "1",
      pageSize: pagination.pageSize?.toString() || "5",
    })
  }

  const onRefresh = () => {
    if (filterRef.current) {
      filterRef.current.filterFunction()
    }
    setSortPaginationValues(null)
    setNeedRefresh(!needRefresh)
  }

  const columns: TableColumnsType<PageData> = [
    {
      title: <strong>{t("Name")}</strong>,
      dataIndex: ["attributes", "name"],
      width: "30%",
      key: "name",
      showSorterTooltip: false,
      sorter: (a, b) => a.attributes.name.localeCompare(b.attributes.name),
      render: (_, record) => (
        <Link
          to={`${Paths.PAGES.EDIT.PATH}/${record.id}`}
          className="text-[#1677ff] cursor-pointer transition duration-300 hover:text-[#0b5ed7] hover:underline text-base"
        >
          {record.attributes.name}
        </Link>
      ),
    },
    {
      title: <strong>{t("Content")}</strong>,
      dataIndex: ["attributes", "content"],
      width: filterValues?.locale === undefined || filterValues?.locale === "all" ? "15%" : "30%",
      key: "content",
      render: content => {
        if (!content) return <div>{t("—")}</div>

        const sanitizedContent = content.replace(/<img[^>]*>/gi, "").replace(/<\/?(h[1-6]|p|a|strong|br)[^>]*>/gi, "")

        const imageRegex = /<img [^>]*src="([^"]+)"[^>]*>/gi
        const maxLength = 100
        const trimmedContent =
          sanitizedContent.length > maxLength ? sanitizedContent.substring(0, maxLength) + "..." : sanitizedContent
        const imageMatches = []
        let match
        while ((match = imageRegex.exec(content)) !== null) {
          imageMatches.push(match[1])
        }

        return (
          <div>
            <div>{trimmedContent}</div>

            {imageMatches.map((src, index) => (
              <img
                key={index}
                src={src}
                alt={`content image ${index}`}
                style={{ width: "100%", maxHeight: "200px", marginTop: "10px" }}
              />
            ))}
          </div>
        )
      },
    },
    {
      title: <strong>{t("Language")}</strong>,
      dataIndex: ["attributes", "locale"],
      width: "15%",
      key: "Language",
      hidden: !(filterValues?.locale === undefined || filterValues?.locale === "all"),
      render: (locale: string) => {
        if (locale) {
          return (
            <div className="flex items-center gap-2">
              <img src={locale === "vi" ? logoVi : logoEn} alt="languages" className="w-5 h-5" />
              <span>{locale === "vi" ? t("Vietnamese") : t("English")}</span>
            </div>
          )
        }
      },
    },
    {
      title: <strong>{t("Create At")}</strong>,
      dataIndex: ["attributes"],
      width: "15%",
      key: "createdAt",
      render: (data: AttributesPage) => {
        const formattedDate = formatDatePost(data.createdAt)
        return (
          <span className="font-normal">
            {data.publishedAt ? t("Published") : t("Last Modified")}
            <br />
            <span>{formattedDate}</span>
          </span>
        )
      },
    },
    {
      title: <strong>{t("Status")}</strong>,
      dataIndex: ["attributes", "publishedAt"],
      key: "status",
      width: "10%",
      render: (publishedAt: string) => {
        return publishedAt ? (
          <Tag color="green" className="p-1 w-full text-center">
            {t("Published")}
          </Tag>
        ) : (
          <Tag color="error" className="p-1 w-full text-center">
            {t("Draft")}
          </Tag>
        )
      },
    },
    {
      title: <strong>{t("Action")}</strong>,
      key: "action",
      width: "15%",
      render: (_, record) => (
        <Space size="small" className="justify-center items-center">
          <Tooltip title={t("Edit")}>
            <Button type="primary" icon={<EditOutlined />} onClick={() => handleEdit(record.id)} />
          </Tooltip>
          <Tooltip title={t("Duplicate")}>
            <Button type="default" icon={<CopyOutlined />} onClick={() => getPageInformaiton(record.id)} />
          </Tooltip>
          <Tooltip title={t("Delete")}>
            <Button danger type="primary" icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} />
          </Tooltip>
        </Space>
      ),
    },
  ]

  return (
    <div className="p-4 ">
      <div className="flex space-x-4 items-center">
        <Title level={2} className="m-0">
          {t("Page")}
        </Title>
        <Button
          onClick={() => navigate(Paths.PAGES.CREATE.PATH)}
          color="primary"
          variant="outlined"
          icon={<PlusOutlined />}
        >
          {t("Add New Page")}
        </Button>
      </div>
      <div className="flex justify-between mt-3">
        <Tabs
          defaultActiveKey="all"
          activeKey={filterTabPage}
          onChange={handleChangeTab}
          items={[
            { label: `${t("All")} (${totalItem})`, key: "all" },
            { label: `${t("Draft")}`, key: "draft" },
            { label: `${t("Published")}`, key: "published" },
          ]}
        />
      </div>
      <div className="items-center">
        <Space className=" space-x-5 flex justify-between">
          <Space className="space-x-3">
            <Select
              defaultValue={t("Bulk Actions")}
              style={{ width: 170 }}
              onChange={value => setBulkAction(value)}
              options={[
                { value: "all", label: t("Bulk Actions") },
                { value: "delete", label: t("Delete") },
              ]}
            />
            <Button onClick={handleApplyBulkAction}>{t("Apply")}</Button>
          </Space>
          <Space>
            <Button onClick={onRefresh} icon={<ReloadOutlined />}>
              {t("Refresh")}
            </Button>
          </Space>
        </Space>
      </div>
      <Card type="inner" style={styles.card}>
        <Filters
          filterRef={filterRef}
          defaultFilterValues={defaultFilterValues}
          onSubmit={onSearch}
          filters={
            [
              {
                label: t("Name"),
                placeholder: t("Find Page Name"),
                name: "name",
                type: "text",
                options: pageNameOptions,
                customStyle: "2xl:!w-[500px] !w-[300px]",
              },
              {
                label: t("Language"),
                placeholder: t("Choose Language"),
                name: "locale",
                type: "select",
                options: [
                  {
                    value: "all",
                    label: (
                      <div className="flex items-center gap-2">
                        <GlobalOutlined className="w-5 h-5" />
                        All
                      </div>
                    ),
                  },
                  {
                    value: "vi",
                    label: (
                      <div className="flex items-center gap-2">
                        <img src={logoVi} alt="Vietnamese" className="w-5 h-5" />
                        VI
                      </div>
                    ),
                  },
                  {
                    value: "en",
                    label: (
                      <div className="flex items-center gap-2">
                        <img src={logoEn} alt="English" className="w-5 h-5" />
                        EN
                      </div>
                    ),
                  },
                ],
                defaultValue: "all",
              },
            ] as FilterComponent[]
          }
        />
      </Card>
      <Table
        className="mt-3  border border-[rgb(229 229 229)]"
        rowKey="id"
        rowSelection={rowSelection}
        columns={columns}
        loading={loading}
        dataSource={dataItem}
        onChange={onChange}
        pagination={{
          pageSize: 20,
          total: (metaData && metaData?.pagination?.total) || 0,
        }}
        rowClassName={(record, index) => (index % 2 === 0 ? "bg-[#f6f7f7] " : "bg-white")}
      />
    </div>
  )
}

export default List

const styles = {
  title: {
    marginTop: 0,
    marginBottom: 0,
  },
  card: {
    marginTop: "1.25rem",
    marginBottom: "1.25rem",
  },
}
